import React, { useState } from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface TransferFromWasteFormProps {
  wasteItem: any;
  onTransferComplete: () => void;
  onCancel: () => void;
}

const TransferFromWasteForm: React.FC<TransferFromWasteFormProps> = ({
  wasteItem,
  onTransferComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    destination_type: wasteItem.source_type || 'supplies',
    quantity: wasteItem.stock_quantity || 0,
    reason: '',
    notes: '',
    destination_location: wasteItem.storage_location || ''
  });

  const destinationTypes = [
    { value: 'supplies', label: 'Supplies Inventory' },
    { value: 'manufactured_products', label: 'Manufactured Products' }
  ];

  const reasons = [
    { value: 'reusable', label: 'Item is Reusable' },
    { value: 'repurpose', label: 'Can be Repurposed' },
    { value: 'mistake', label: 'Transferred by Mistake' },
    { value: 'restored', label: 'Item Restored/Repaired' },
    { value: 'needed', label: 'Needed for Production' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.quantity <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }

    if (formData.quantity > wasteItem.stock_quantity) {
      alert(`Cannot transfer more than available quantity (${wasteItem.stock_quantity})`);
      return;
    }

    if (!formData.reason) {
      alert('Please select a reason for transferring back to active inventory');
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('transfer_from_waste', {
        p_user_id: user?.id,
        p_waste_id: wasteItem.id,
        p_destination_type: formData.destination_type,
        p_quantity: formData.quantity,
        p_reason: formData.reason,
        p_notes: formData.notes || null,
        p_destination_location: formData.destination_location || null
      });

      if (error) throw error;

      alert(`Successfully transferred ${formData.quantity} units back to ${formData.destination_type === 'supplies' ? 'Supplies' : 'Manufactured Products'}`);
      onTransferComplete();
    } catch (error: any) {
      console.error('Error transferring from waste:', error);
      alert(error.message || 'Failed to transfer from waste. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-center">
          <RotateCcw className="h-5 w-5 text-green-600 mr-2" />
          <CardTitle>Transfer Back to Active Inventory</CardTitle>
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">{wasteItem.original_project_name}</h4>
            <p className="text-sm text-blue-700">Item ID: {wasteItem.item_id}</p>
            {wasteItem.sku && (
              <p className="text-sm text-blue-700">SKU: {wasteItem.sku}</p>
            )}
            <p className="text-sm text-blue-700">
              Available Quantity: {wasteItem.stock_quantity} units
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Transfer Back to Active Inventory
                </p>
                <p className="text-sm text-green-700 mt-1">
                  This will move the specified quantity from R&D Waste back to active inventory,
                  making it available for use or sale again.
                </p>
              </div>
            </div>
          </div>

          <Select
            label="Destination Inventory"
            options={[
              { value: '', label: 'Select destination' },
              ...destinationTypes
            ]}
            value={formData.destination_type}
            onChange={(value) => setFormData({ ...formData, destination_type: value })}
            required
            fullWidth
          />

          <Input
            label="Quantity to Transfer"
            type="number"
            min="1"
            max={wasteItem.stock_quantity}
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
            helperText={`Maximum: ${wasteItem.stock_quantity} units`}
            required
            fullWidth
          />

          <Input
            label="Destination Location (Optional)"
            type="text"
            value={formData.destination_location}
            onChange={(e) => setFormData({ ...formData, destination_location: e.target.value })}
            placeholder="e.g., Warehouse A, Lab 3"
            fullWidth
          />

          <Select
            label="Reason for Transfer"
            options={[
              { value: '', label: 'Select a reason' },
              ...reasons
            ]}
            value={formData.reason}
            onChange={(value) => setFormData({ ...formData, reason: value })}
            required
            fullWidth
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
              placeholder="Add any additional details about this transfer..."
            />
          </div>

          {wasteItem.source_type && wasteItem.source_id && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> This item was originally from {wasteItem.source_type === 'supplies' ? 'Supplies' : 'Manufactured Products'}.
                {formData.destination_type === wasteItem.source_type &&
                  ' It will be added back to the original item if it still exists, or a new entry will be created.'}
              </p>
            </div>
          )}
        </CardContent>

        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
            disabled={formData.quantity <= 0 || formData.quantity > wasteItem.stock_quantity}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Transfer Back
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default TransferFromWasteForm;
